/**
 * O serviço que junta o relógio e a prova — card 1864753210497959381.
 *
 * ⚠️ **A armadilha que decide o desenho deste arquivo**: `engine.doc.state$` só emite quando
 * o estado MUDA. Numa fila travada nada muda — é exatamente essa a definição de travada —,
 * então nenhuma amostra nova chega. Um relógio que só avançasse no `subscribe` **nunca
 * passaria do limiar**, e esta entrega inteira ficaria verde sobre o defeito que ela existe
 * para encontrar.
 *
 * Por isso o tempo vem de um ticker próprio, e o `state$` só atualiza a amostra.
 */

import { LiveData, Service } from '@toeverything/infra';
import {
  decodeStateVector,
  encodeStateVector,
  encodeStateVectorFromUpdate,
} from 'yjs';

import type { GlobalContextService } from '../../global-context';
import type { WorkspaceService } from '../../workspace';
import {
  provarContraOServidor,
  type Decodificador,
  type ResultadoDaProva,
} from '../prova-do-servidor';
import {
  avaliar,
  ESTADO_INICIAL,
  mereceAviso,
  temFilaPendente,
  type AmostraDoMotor,
  type EstadoDoRelogio,
} from '../relogio-da-fila';

/** Barato: uma função pura sobre dois objetos pequenos. */
const INTERVALO_DO_RELOGIO_MS = 2_000;

/**
 * Em repouso a prova existe para o caso que o motor NÃO reporta: `synced: true` com o
 * servidor atrasado. É raro o bastante para 5 minutos, e é o intervalo que mantém a
 * analítica de visualização (`recordDocView`) num patamar comparável ao de alguém lendo.
 */
const INTERVALO_DA_PROVA_EM_REPOUSO_MS = 300_000;

/** Sob suspeita a prova é o que transforma "o indicador travou" em "faltam N alterações". */
const INTERVALO_DA_PROVA_SOB_SUSPEITA_MS = 60_000;

/**
 * O `yjs` mora AQUI, e não em `prova-do-servidor.ts`.
 *
 * Este arquivo já é inexecutável fora do bundle (framework, LiveData, engine); aquele não
 * precisava ser, e a decisão inteira — inclusive os três estados do veredito — se testa
 * sem `yarn install` por causa desta separação.
 */
const DECODIFICADOR: Decodificador = {
  deVetor: bin => decodeStateVector(bin),
  deAtualizacao: bin => decodeStateVector(encodeStateVectorFromUpdate(bin)),
};

const AMOSTRA_VAZIA: AmostraDoMotor = {
  pendentes: 0,
  sincronizado: true,
  retentando: false,
  erro: null,
  temServidor: false,
  online: true,
};

export class SaudeDaSincronizacaoService extends Service {
  readonly estado$ = new LiveData<EstadoDoRelogio>(ESTADO_INICIAL);
  readonly prova$ = new LiveData<ResultadoDaProva | null>(null);
  readonly provando$ = new LiveData<boolean>(false);

  private workspaceService!: WorkspaceService;
  private globalContextService!: GlobalContextService;

  private amostra: AmostraDoMotor = AMOSTRA_VAZIA;
  private ultimaProvaEm = 0;
  private provaEmCurso: AbortController | null = null;

  constructor(
    workspaceService: WorkspaceService,
    globalContextService: GlobalContextService
  ) {
    super();
    this.workspaceService = workspaceService;
    this.globalContextService = globalContextService;
    this.iniciar();
  }

  // ---------------------------------------------------------------------------------------

  private iniciar() {
    this.amostra = { ...AMOSTRA_VAZIA, temServidor: this.temServidor() };
    this.assinarOMotor();
    this.assinarARede();

    const ticker = setInterval(
      () => this.bater(Date.now()),
      INTERVALO_DO_RELOGIO_MS
    );
    this.disposables.push(() => clearInterval(ticker));
    this.disposables.push(() => this.provaEmCurso?.abort());

    this.bater(Date.now());
  }

  private temServidor(): boolean {
    try {
      return this.workspaceService.workspace.flavour !== 'local';
    } catch {
      return false;
    }
  }

  private assinarOMotor() {
    // O engine lança enquanto o worker não subiu. Tentar de novo no ticker é melhor que
    // derrubar a montagem do workspace por causa de um aviso.
    let assinatura: { unsubscribe: () => void } | null = null;

    const tentar = () => {
      if (assinatura) return;
      try {
        const estado$ = this.workspaceService.workspace.engine.doc.state$;
        assinatura = estado$.subscribe({
          next: estado => {
            this.amostra = {
              pendentes: estado.syncing ?? 0,
              sincronizado: !!estado.synced,
              retentando: !!estado.syncRetrying,
              erro: estado.syncErrorMessage ?? null,
              temServidor: this.temServidor(),
              online: this.estaOnline(),
            };
          },
          error: () => {
            /* o ticker continua; a amostra fica na última conhecida */
          },
        });
      } catch {
        /* engine ainda não iniciado — o ticker tenta de novo */
      }
    };

    tentar();
    this.reassinar = tentar;
    this.disposables.push(() => assinatura?.unsubscribe());
  }

  private reassinar: () => void = () => {};

  private estaOnline(): boolean {
    return typeof navigator === 'undefined' ? true : navigator.onLine !== false;
  }

  private assinarARede() {
    if (typeof window === 'undefined') return;
    const mudou = () => {
      this.amostra = { ...this.amostra, online: this.estaOnline() };
      this.bater(Date.now());
    };
    window.addEventListener('online', mudou);
    window.addEventListener('offline', mudou);
    this.disposables.push(() => {
      window.removeEventListener('online', mudou);
      window.removeEventListener('offline', mudou);
    });
  }

  // ---------------------------------------------------------------------------------------

  private bater(agora: number) {
    this.reassinar();

    const anterior = this.estado$.value;
    const novo = avaliar(
      { ...this.amostra, online: this.estaOnline() },
      anterior,
      agora
    );

    // Emitir só quando muda a gravidade ou quando a faixa está na tela (aí o tempo decorrido
    // precisa acompanhar). Emitir sempre custaria um render a cada 2 s pelo resto da sessão.
    if (novo.gravidade !== anterior.gravidade || mereceAviso(novo)) {
      this.estado$.next(novo);
    }

    // A prova some quando a fila desce: uma linha dizendo "faltam 12 alterações" que sobrevive
    // ao conserto é a Regra 8.72.1 — erro sem hora se lê como erro de agora.
    if (novo.gravidade === 'em-dia' && this.prova$.value?.veredito === 'servidor-atrasado') {
      this.prova$.next(null);
    }

    this.talvezProvar(novo, agora);
  }

  private talvezProvar(estado: EstadoDoRelogio, agora: number) {
    if (estado.gravidade === 'desligado') return;

    const intervalo = mereceAviso(estado)
      ? INTERVALO_DA_PROVA_SOB_SUSPEITA_MS
      : INTERVALO_DA_PROVA_EM_REPOUSO_MS;

    if (agora - this.ultimaProvaEm < intervalo) return;

    void this.provar();
  }

  /**
   * Pergunta ao servidor agora. É o que o botão do aviso chama, e é público de propósito:
   * quem desconfia deve conseguir conferir sem esperar o próximo ciclo.
   */
  async provar(): Promise<ResultadoDaProva | null> {
    if (this.provando$.value) return this.prova$.value;

    const alvo = this.vetorLocalDoDocAberto();
    if (!alvo) {
      // Sem documento aberto não há o que provar — e inventar um veredito aqui seria pior
      // que não ter nenhum.
      return this.prova$.value;
    }

    this.ultimaProvaEm = Date.now();
    this.provando$.next(true);
    this.provaEmCurso?.abort();
    const controle = new AbortController();
    this.provaEmCurso = controle;

    try {
      const resultado = await provarContraOServidor({
        workspaceId: this.workspaceService.workspace.id,
        docId: alvo.docId,
        vetorLocal: alvo.vetor,
        decodificador: DECODIFICADOR,
        sinal: controle.signal,
      });
      this.prova$.next(resultado);
      return resultado;
    } catch {
      return this.prova$.value;
    } finally {
      this.provando$.next(false);
      if (this.provaEmCurso === controle) this.provaEmCurso = null;
    }
  }

  /**
   * O vetor de estado do documento ABERTO, lido do Y.Doc em memória.
   *
   * ⚠️ Em memória, e não do store local, de propósito: a pergunta que importa é *"o servidor
   * tem o que está NESTA TELA?"*. Ler o store local responderia "o servidor tem o que o
   * store local tem" — e se o próprio store local estivesse atrasado, a resposta seria um
   * verde sobre trabalho perdido.
   */
  private vetorLocalDoDocAberto(): { docId: string; vetor: Uint8Array } | null {
    try {
      const docId = this.globalContextService.globalContext.docId.get();
      if (!docId) return null;
      const doc = this.workspaceService.workspace.docCollection.getDoc(docId);
      const ydoc = doc?.spaceDoc;
      if (!ydoc) return null;
      return { docId, vetor: encodeStateVector(ydoc) };
    } catch {
      return null;
    }
  }

  /** Há trabalho que ainda não saiu deste navegador? É o que o `beforeunload` pergunta. */
  temTrabalhoNaoSalvo(): boolean {
    return temFilaPendente(this.estado$.value);
  }
}
